/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { csAccountSwaggerToCSAccountStore } from 'src/core/handlers/azure/azureMappers';
import { getAuthService } from 'src/core/services/authService';
import { getAzureService } from 'src/core/services/azure/azureService';
import { getTokensService } from 'src/core/services/tokensService';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { AzureTenantStore } from 'src/core/stores/AzureTenantStore';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { isDevelopmentEnvironment } from 'src/core/utils/api/environment';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type TokensHandlerGroup = ReturnType<typeof createTokensHandlerGroup>;

/**
 * Update the csAccount store and account tokens for
 * the csAccountPath in session if exists.
 */
export const updateCsAccountStoreInSession = async (rootStore: RootStore) => {
    const {
        userTokens: { armToken },
        csAccountStore,
        tenantId
    } = rootStore.azureStore.session;

    if (!csAccountStore) {
        return;
    }

    const csAccountSwagger = await getAzureService().getAzureCsAccountByPath({ tenantToken: armToken, path: csAccountStore.path });
    const updatedCsAccountStore = csAccountSwaggerToCSAccountStore(csAccountSwagger);

    rootStore.azureStore.session.setCsAccountStore(updatedCsAccountStore);

    const serviceKey = await getTokensService().getServiceKey({
        tenantId,
        armToken,
        principalId: rootStore.userStore.principalId,
        resourcePath: updatedCsAccountStore.path,
        resourceKind: updatedCsAccountStore.kind
    });
    rootStore.azureStore.session.setServiceKey(serviceKey);
};

/**
 * Creates a new handler for all token related
 * functionalities in the system.
 */
export const createTokensHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    /**
     * Switches the current tenant in the Azure session store.
     *
     * @param param0.id The id of the tenant to switch to.
     */
    const switchAzureTenantInSession = async ({ id }: HandlerParams<Pick<AzureTenantStore, 'id'>>) => {
        rootStore.azureStore.session.setTenantId(id);
        rootStore.azureStore.session.setSubscriptionId('');
        rootStore.azureStore.session.setCsAccountStore(null);
        rootStore.azureStore.session.setUserTokens({ armToken: null, storageToken: null });
        rootStore.azureStore.session.setServiceKey({ keyType: 'none', value: null, userRole: 'none', displayedUserRole: 'none' });
        rootStore.azureStore.session.setTranslatorStorageAccount(null);

        getAuthService().login({ tenantId: id });
    };

    /**
     * Switches the current subscription in the Azure session store.
     *
     * @param param0.id The id of the subscription to switch to.
     */
    const switchAzureSubscriptionInSession = async ({ id }: HandlerParams<Pick<AzureSubscriptionStore, 'id'>>) => {
        rootStore.azureStore.session.setSubscriptionId(id);
        rootStore.azureStore.session.setCsAccountStore(null);
        rootStore.azureStore.session.setTranslatorStorageAccount(null);
    };

    /**
     * Switches the current CS Account in the Azure session store and updates its tokens.
     *
     * @param param0.csAccountStore The cs account to switch to.
     */
    const switchAzureCsAccountInSession = async ({
        csAccountPath,
        csAccountStore,
        newAccount,
        context: { forceRefresh }
    }: HandlerParams<{ csAccountPath?: string; csAccountStore?: AzureCsAccountStore; newAccount?: boolean }>) => {
        const azureSession = rootStore.azureStore.session;
        const { tenantId, userTokens } = azureSession;

        if (!csAccountStore && csAccountPath !== azureSession.csAccountStore?.path) {
            const csAccountStoreSwagger = await getAzureService().getAzureCsAccountByPath({
                tenantToken: userTokens.armToken,
                path: csAccountPath
            });
            csAccountStore = csAccountSwaggerToCSAccountStore(csAccountStoreSwagger);
        }

        if (!forceRefresh && azureSession.csAccountStore?.id === csAccountStore.id && userTokens.armToken) {
            return;
        }

        if (csAccountStore.subscriptionId) {
            azureSession.setSubscriptionId(csAccountStore.subscriptionId);
        }

        if (newAccount && !isDevelopmentEnvironment()) {
            // Sleeps for 5 seconds if the account we're switching
            // to is a new one, otherwise subsequent calls using this account
            // would fail because the new account hasn't been propagated to CIG yet.
            await new Promise(res => setTimeout(res, 1000 * 5));
        }

        let serviceKey: ServiceKey = null;
        let err: Error = null;

        try {
            serviceKey = await getTokensService().getServiceKey({
                tenantId,
                armToken: userTokens.armToken,
                resourcePath: csAccountStore.path,
                resourceKind: csAccountStore.kind,
                principalId: rootStore.userStore.principalId
            });
        } catch (e) {
            err = e;
        }

        transaction(() => {
            rootStore.azureStore.session.setCsAccountStore(new AzureCsAccountStore(csAccountStore.id, { ...csAccountStore }));
            rootStore.azureStore.session.setTranslatorStorageAccount(null);
            if (serviceKey) {
                rootStore.azureStore.session.setServiceKey(serviceKey);
            }
        });

        if (err) {
            throw err;
        }

        getStorage('localStorage').setData(LuisConstants.StorageKeys.LanguageStudioResourceAcknowledgeStorageKey, 'false');
    };

    /**
     * Updates the translator storage account.
     *
     * @param param0.storageAccountName The storage account name to be saved in the document translator tool configs.
     */
    const updateTranslatorStorageAccountInSession = async ({ storageAccountName }: HandlerParams<{ storageAccountName: string }>) => {
        rootStore.azureStore.session.setTranslatorStorageAccount(storageAccountName);
    };

    return {
        switchAzureTenantInSession,
        switchAzureCsAccountInSession,
        switchAzureSubscriptionInSession,
        updateTranslatorStorageAccountInSession
    };
};
